import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import clsx from "clsx";

import Launch from "@material-ui/icons/Launch";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import LastRaceIcon from "components/Icons/LastRace";
import Link from "components/Link";
import CustomRaceIcon from "components/Icons/CustomRace";
import MatchesCount from "components/MatchesCount";
import routes from "common/routes";
import { setSelectedRaceId } from "store/actions/conditionBookPage";
import useStyles from "./styles";

interface IProps {
  matchedHorses: racehorse360.IHorse[] | null;
  selectedRaceId: string;
}

const MatchesDetails = React.memo((props: IProps) => {
  const { matchedHorses, selectedRaceId } = props;
  const dispatch = useDispatch();
  const classes = useStyles();

  const [isExpanded, setIsExpanded] = useState<boolean>(
    Boolean(matchedHorses.length)
  );
  const [shouldSaveRaceId, setShouldSaveRaceId] = useState(false);

  const saveSelectedRaceId = () => {
    setShouldSaveRaceId(true);
    dispatch(setSelectedRaceId(selectedRaceId));
  };

  const lastRaceMatchesHorses = matchedHorses
    ? matchedHorses.filter(
        (horse: racehorse360.IHorse) =>
          horse.areLastRacePreferredConditionsSelected
      )
    : [];
  const customRaceMatchesHorses = matchedHorses
    ? matchedHorses.filter(
        (horse: racehorse360.IHorse) =>
          !horse.areLastRacePreferredConditionsSelected
      )
    : [];
  const hasLastRaceMatches = Boolean(lastRaceMatchesHorses.length);
  const hasCustomMatches = Boolean(customRaceMatchesHorses.length);

  const lastRaceConditionsMatches = lastRaceMatchesHorses.map(
    (item: racehorse360.IHorse) => {
      return (
        <Link
          key={item.id}
          to={routes.horseDetails.path.replace(":horseId", String(item.id))}
          className={clsx(classes.matchesItem, classes.lastRaceMatch)}
          onClick={saveSelectedRaceId}
        >
          <span className={classes.matchesText}>{item.name}</span>
          <Launch className={classes.horseDetailsLinkIcon} />
        </Link>
      );
    }
  );

  const customConditionsMatches = customRaceMatchesHorses.map(
    (item: racehorse360.IHorse) => {
      return (
        <Link
          key={item.id}
          to={routes.horseDetails.path.replace(":horseId", String(item.id))}
          className={classes.matchesItem}
          onClick={saveSelectedRaceId}
        >
          <span className={classes.matchesText}>{item.name}</span>
          <Launch
            className={clsx(
              classes.horseDetailsLinkIcon,
              classes.customMatches
            )}
          />
        </Link>
      );
    }
  );

  useEffect(() => {
    !shouldSaveRaceId && dispatch(setSelectedRaceId(null));
  }, [shouldSaveRaceId]);

  useEffect(() => {
    setIsExpanded(Boolean(matchedHorses.length));
  }, [matchedHorses]);

  const handleChangeAccordionState = (event, expanded) => {
    setIsExpanded(expanded);
  };

  return (
    <Accordion
      square
      expanded={isExpanded}
      onChange={handleChangeAccordionState}
      classes={{
        root: classes.expRoot,
        disabled: classes.expDisabled
      }}
    >
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={<ExpandMoreIcon />}
        classes={{
          root: classes.sumRoot,
          expanded: classes.sumRootExpanded
        }}
        disabled={!matchedHorses.length}
      >
        <div className={classes.summaryContent}>
          <div className={classes.summaryTitle}>
            <span className={classes.myMatches}>My Matches</span>
            <MatchesCount matchedHorsesCount={matchedHorses.length} />
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {hasLastRaceMatches && (
          <div>
            <div className={classes.matchesHeader}>
              <LastRaceIcon className={classes.lastRaceIconDetails} />
              <span className={classes.matchesHeaderText}>
                Last Race Conditions Matches
              </span>
            </div>
            <div className={classes.matchesList}>
              {lastRaceConditionsMatches}
            </div>
          </div>
        )}
        {hasCustomMatches && (
          <div>
            <div className={classes.matchesHeader}>
              <CustomRaceIcon className={classes.customIconDetails} />
              <span className={classes.matchesHeaderText}>
                Custom Conditions Matches
              </span>
            </div>
            <div className={classes.matchesList}>{customConditionsMatches}</div>
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
});

export default MatchesDetails;
